/* ==========================================================================
   VELAREMILANO — interaction layer
   No dependencies. Everything degrades to a static page if JS is off.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var root = document.documentElement;

  // Belt and braces: theme.liquid sets this inline so the hidden state applies
  // before first paint. Re-assert it here for any host that strips that.
  root.classList.add('vm-anim');

  /* ----------------------------------------------------------------------
     Text splitting — words / chars / lines, wrapped so we can animate them
     ---------------------------------------------------------------------- */
  function splitNode(el, mode, step, base) {
    if (el.dataset.vmSplitDone) return;
    el.dataset.vmSplitDone = '1';

    var i = 0;

    function wrapText(node, out) {
      var text = node.nodeValue;
      if (mode === 'chars') {
        // Each character animates on its own, but they are grouped per word.
        // `.vm-fly__c` is inline-block, and the browser may break a line
        // between any two adjacent atomic inlines — so emitting bare character
        // spans removes every word boundary from the headline. Once fitText()
        // hands back to the CSS clamp below its threshold and wrapping is
        // allowed again, that let the hero break mid-word as "MODO TU / O".
        // The word wrapper is the only thing holding a word together.
        var chunks = text.split(/(\s+)/);
        chunks.forEach(function (part) {
          if (!part) return;
          if (/^\s+$/.test(part)) { out.appendChild(document.createTextNode(part)); return; }
          var word = document.createElement('span');
          word.className = 'vm-fly__w';
          for (var c = 0; c < part.length; c++) {
            var s = document.createElement('span');
            s.className = 'vm-fly__c';
            s.style.setProperty('--vm-d', base + i * step + 'ms');
            s.textContent = part[c];
            word.appendChild(s);
            i++;
          }
          out.appendChild(word);
        });
      } else {
        var parts = text.split(/(\s+)/);
        parts.forEach(function (p) {
          if (!p) return;
          if (/^\s+$/.test(p)) { out.appendChild(document.createTextNode(p)); return; }
          var w = document.createElement('span');
          w.className = 'vm-whip__w';
          w.style.setProperty('--vm-d', base + i * step + 'ms');
          w.textContent = p;
          out.appendChild(w);
          i++;
        });
      }
    }

    function walk(src, dest) {
      Array.prototype.slice.call(src.childNodes).forEach(function (n) {
        if (n.nodeType === 3) {
          wrapText(n, dest);
        } else if (n.nodeType === 1) {
          var clone = n.cloneNode(false);
          dest.appendChild(clone);
          walk(n, clone);
        }
      });
    }

    var frag = document.createDocumentFragment();
    walk(el, frag);
    el.innerHTML = '';
    el.appendChild(frag);
    el.classList.add(mode === 'chars' ? 'vm-fly' : 'vm-whip');
  }

  function prepareSplits(scope) {
    (scope || document).querySelectorAll('[data-vm-split]').forEach(function (el) {
      var mode = el.getAttribute('data-vm-split') || 'words';
      var step = parseFloat(el.getAttribute('data-vm-step') || (mode === 'chars' ? 26 : 55));
      var base = parseFloat(el.getAttribute('data-vm-delay') || 0);
      splitNode(el, mode, step, base);
    });
  }


  /* ----------------------------------------------------------------------
     Fit-to-width display type — scales a headline so one line spans the
     full container, the way the reference layout sets its big words.
     ---------------------------------------------------------------------- */
  function fitText() {
    var nodes = document.querySelectorAll('[data-vm-fit]');
    if (!nodes.length) return;
    nodes.forEach(function (el) {
      var box = el.parentElement;
      if (!box) return;
      var cs = getComputedStyle(box);
      var avail = box.clientWidth -
        parseFloat(cs.paddingLeft || 0) - parseFloat(cs.paddingRight || 0);

      // When the headline is a grid/flex item it has to fit *its own cell*, not
      // the whole parent. Measuring the parent let a nowrap collection title
      // overflow its column and run underneath the description beside it.
      var disp = getComputedStyle(el).display;
      var stretches = /^(block|flex|grid|list-item|table)$/.test(disp);
      if (stretches) {
        var own = el.getBoundingClientRect().width;
        if (own > 0) avail = Math.min(avail, own);
      }
      if (avail <= 0) return;

      // Below this width a single fitted line gets too small to read — hand
      // back to the CSS clamp and let the headline wrap.
      var from = parseFloat(el.getAttribute('data-vm-fit-from')) || 640;
      if (avail < from) {
        el.style.fontSize = '';
        el.classList.add('vm-fit-off');
        return;
      }
      el.classList.remove('vm-fit-off');

      // Measure the natural single-line width at a known size.
      //
      // Measure the *text*, not the element box. Switching the box to
      // inline-block to make it shrink-to-fit does not work for a grid or flex
      // item — the child is blockified back to `block`, so the box stays the
      // full column width and the measurement comes back equal to the space
      // available. That made every headline compute the same font size and
      // simply overflow its column (collection titles ran under the aside).
      var prevWS = el.style.whiteSpace;
      var prevMax = el.style.maxWidth;
      el.style.whiteSpace = 'nowrap';
      el.style.maxWidth = 'none';   // a max-width would clamp the measurement
      el.style.fontSize = '100px';
      var natural = 0;
      try {
        var range = document.createRange();
        range.selectNodeContents(el);
        natural = range.getBoundingClientRect().width;
      } catch (e) { /* fall through to the box measurement */ }
      if (!natural) natural = el.getBoundingClientRect().width;
      el.style.whiteSpace = prevWS;
      el.style.maxWidth = prevMax;

      if (!natural) { el.style.fontSize = ''; return; }

      var size = (avail / natural) * 100 * 0.995;
      var max = parseFloat(el.getAttribute('data-vm-fit')) || 0;
      var min = parseFloat(el.getAttribute('data-vm-fit-min')) || 22;
      if (max) size = Math.min(size, max);
      size = Math.max(size, min);
      el.style.fontSize = size.toFixed(2) + 'px';
    });
  }

  function watchFit() {
    if (!document.querySelector('[data-vm-fit]')) return;
    fitText();

    // Web fonts change the metrics, so re-fit as each one lands.
    if (document.fonts) {
      if (document.fonts.load) {
        Promise.all([
          document.fonts.load('400 100px Anton'),
          document.fonts.load('600 1em Archivo'),
          document.fonts.load('700 1em "Courier Prime"')
        ]).then(fitText).catch(function () {});
      }
      if (document.fonts.ready) document.fonts.ready.then(fitText);
      if (document.fonts.addEventListener) {
        document.fonts.addEventListener('loadingdone', fitText);
      }
    }
    setTimeout(fitText, 350);
    setTimeout(fitText, 1200);

    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(fitText, 120);
    });
  }

  /* ----------------------------------------------------------------------
     Reveal on scroll
     ---------------------------------------------------------------------- */
  /* ----------------------------------------------------------------------
     Reveal on scroll.

     Visible is the default state; an element is hidden only while it carries
     `.vm-hidden`, and revealing means removing that class. Removing a class
     takes effect whether or not a frame is ever rendered, so nothing can be
     stranded invisible — which is exactly what happened when the page was
     opened in a background tab under the previous add-a-class-to-reveal model.
     ---------------------------------------------------------------------- */
  var io;
  var animationsOff =
    reduced ||
    !('IntersectionObserver' in window) ||
    /[?&]noanim\b/.test(window.location.search);

  function show(el) {
    el.classList.remove('vm-hidden');
  }

  function hide(el) {
    if (animationsOff) return;
    el.classList.add('vm-hidden');
  }

  function observe(scope) {
    var nodes = (scope || document).querySelectorAll(
      '[data-vm-in], [data-vm-split], .vm-rv'
    );

    if (animationsOff) {
      nodes.forEach(show);
      return;
    }

    if (!io) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          show(e.target);
          io.unobserve(e.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }

    nodes.forEach(function (el) {
      if (!el.classList.contains('vm-hidden')) return;  // already revealed
      io.observe(el);
    });
  }

  /* Put elements into their hidden start state.

     Only ever arms while the document is actually being rendered. A hidden tab
     produces no frames, so a transition started there freezes part-way and the
     element stays invisible until something forces it — and animating content
     nobody is looking at buys nothing anyway. If the page loads hidden we skip
     the entrance for that load and simply show everything. */
  function arm(scope) {
    clearTimeout(window.__vmAnimFallback);

    if (animationsOff || document.visibilityState !== 'visible') return;

    // Order matters: arm and hide FIRST, drop the pre-paint gate after. Doing
    // it the other way round flashes the elements visible and starts a
    // transition we never wanted.
    root.classList.add('vm-armed');
    (scope || document)
      .querySelectorAll('[data-vm-in], [data-vm-split], .vm-rv')
      .forEach(hide);
  }

  function unlockPaint() {
    root.classList.remove('vm-anim');
  }

  /* Backstop.

     The IntersectionObserver is the nice path — it gives the staggered timing.
     But it stops delivering callbacks whenever the document isn't being
     rendered, and it never resumes for elements it already had queued. A
     partial reveal is therefore NOT evidence that it is working: an earlier
     version checked `hidden < total` and concluded "observer is fine", which
     stranded 72 elements permanently.

     So: sweep by geometry instead. Anything currently within a viewport-and-a-
     bit gets shown, no matter what the observer did or didn't do. Runs on
     scroll, resize, visibility change and a couple of timers after boot. */
  function sweep() {
    var hidden = document.querySelectorAll('.vm-hidden');
    if (!hidden.length) return;
    var vh = window.innerHeight || 800;
    hidden.forEach(function (el) {
      // Anything that has reached — or already passed — the fold is revealed.
      // No bottom check: an element scrolled quickly past must not stay hidden.
      if (el.getBoundingClientRect().top < vh * 1.05) show(el);
    });
  }

  /* An element with no `.vm-hidden` that still computes to transparent is a
     transition frozen mid-flight (the tab stopped rendering while it ran).
     Drop the transition so it lands on its final value. */
  function unfreeze() {
    var stuck = [];
    document.querySelectorAll('[data-vm-in], [data-vm-split], .vm-rv').forEach(function (el) {
      if (el.classList.contains('vm-hidden')) return;
      if (parseFloat(getComputedStyle(el).opacity) < 0.02) stuck.push(el);
    });
    if (!stuck.length) return;
    stuck.forEach(function (el) {
      el.style.transition = 'none';
      el.style.opacity = '';
      el.style.transform = '';
      el.style.clipPath = '';
      el.style.filter = '';
    });
    requestAnimationFrame(function () {
      stuck.forEach(function (el) { el.style.transition = ''; });
    });
  }

  function revealSafety() {
    var pending = false;
    function queueSweep() {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        sweep();
      });
    }

    window.addEventListener('scroll', queueSweep, { passive: true });
    window.addEventListener('resize', queueSweep);
    window.addEventListener('pageshow', queueSweep);

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState !== 'visible') return;
      observe();
      queueSweep();
      setTimeout(unfreeze, 120);
    });

    // Catch anything above the fold that the observer missed at boot.
    setTimeout(sweep, 300);
    setTimeout(sweep, 1500);
    setTimeout(function () { sweep(); unfreeze(); }, 4000);
  }

  /* Stagger children of [data-vm-stagger] */
  function applyStagger(scope) {
    (scope || document).querySelectorAll('[data-vm-stagger]').forEach(function (p) {
      if (p.dataset.vmStaggerDone) return;
      p.dataset.vmStaggerDone = '1';
      var step = parseFloat(p.getAttribute('data-vm-stagger')) || 90;
      var base = parseFloat(p.getAttribute('data-vm-delay')) || 0;
      Array.prototype.forEach.call(p.children, function (c, i) {
        c.style.setProperty('--vm-d', base + i * step + 'ms');
      });
    });
  }

  /* ----------------------------------------------------------------------
     Header — hide on scroll down, show on scroll up
     ---------------------------------------------------------------------- */
  function header() {
    var h = document.querySelector('[data-vm-header]');
    if (!h) return;
    var last = window.scrollY;
    var ticking = false;

    function measure() {
      root.style.setProperty('--vm-header-h', h.offsetHeight + 'px');
    }
    measure();
    window.addEventListener('resize', measure);

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var y = window.scrollY;
        h.classList.toggle('is-stuck', y > 8);
        if (!document.body.classList.contains('vm-locked')) {
          if (y > last && y > 260) h.classList.add('is-hidden');
          else h.classList.remove('is-hidden');
        }
        last = y;
        ticking = false;
      });
    }, { passive: true });
  }

  /* ----------------------------------------------------------------------
     Mobile drawer
     ---------------------------------------------------------------------- */
  function drawer() {
    var btn = document.querySelector('[data-vm-burger]');
    var dr = document.querySelector('[data-vm-drawer]');
    if (!btn || !dr) return;

    dr.querySelectorAll('.vm-drawer__link').forEach(function (l, i) {
      l.style.setProperty('--vm-d', 90 + i * 65 + 'ms');
    });

    // The closed drawer is hidden only by clip-path, so its links stay in the
    // focus order and the a11y tree unless something says otherwise. `inert`
    // does both without touching `visibility`, which WebKit fails to repaint
    // through on this composited fixed layer. Safari < 16.4 ignores `inert`
    // and simply keeps the old behaviour — nothing renders wrong either way.
    dr.inert = !dr.classList.contains('is-open');

    function set(open) {
      dr.classList.toggle('is-open', open);
      dr.inert = !open;
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.body.classList.toggle('vm-locked', open);
      document.body.style.overflow = open ? 'hidden' : '';
    }
    btn.addEventListener('click', function () {
      set(!dr.classList.contains('is-open'));
    });
    dr.querySelectorAll('[data-vm-drawer-close], .vm-drawer__link').forEach(function (el) {
      el.addEventListener('click', function () { set(false); });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && dr.classList.contains('is-open')) set(false);
    });
  }

  /* ----------------------------------------------------------------------
     Marquee — duplicate the group so the -50% loop is seamless
     ---------------------------------------------------------------------- */
  function marquees() {
    document.querySelectorAll('[data-vm-marquee]').forEach(function (m) {
      var track = m.querySelector('.vm-marquee__track');
      if (!track || track.dataset.vmCloned) return;
      var group = track.querySelector('.vm-marquee__group');
      if (!group) return;

      // Repeat until the group is at least as wide as the viewport, then mirror it.
      var guard = 0;
      while (group.offsetWidth < window.innerWidth && guard < 12) {
        group.appendChild(group.firstElementChild.cloneNode(true));
        group.appendChild(group.children[1].cloneNode(true));
        guard++;
      }
      track.appendChild(group.cloneNode(true));
      track.dataset.vmCloned = '1';

      var speed = parseFloat(m.getAttribute('data-vm-marquee')) || 26;
      m.style.setProperty('--vm-mq-speed', speed + 's');
    });
  }

  /* ----------------------------------------------------------------------
     Parallax drift on hero / card media
     ---------------------------------------------------------------------- */
  function parallax() {
    if (reduced) return;
    var items = Array.prototype.slice.call(document.querySelectorAll('[data-vm-parallax]'));
    if (!items.length) return;
    var ticking = false;

    function run() {
      var vh = window.innerHeight;
      items.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return;
        var amount = parseFloat(el.getAttribute('data-vm-parallax')) || 8;
        var progress = (r.top + r.height / 2 - vh / 2) / vh; // -1 .. 1
        el.style.transform = 'translate3d(0,' + (progress * amount * -1).toFixed(2) + '%,0)';
      });
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(run);
    }, { passive: true });
    run();
  }

  /* ----------------------------------------------------------------------
     Magnetic buttons
     ---------------------------------------------------------------------- */
  function magnets() {
    if (reduced || window.matchMedia('(hover: none)').matches) return;
    document.querySelectorAll('[data-vm-magnet]').forEach(function (el) {
      var strength = parseFloat(el.getAttribute('data-vm-magnet')) || 0.28;
      el.addEventListener('mousemove', function (e) {
        var r = el.getBoundingClientRect();
        var x = (e.clientX - r.left - r.width / 2) * strength;
        var y = (e.clientY - r.top - r.height / 2) * strength;
        el.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0)';
      });
      el.addEventListener('mouseleave', function () { el.style.transform = ''; });
    });
  }

  /* ----------------------------------------------------------------------
     Scroll progress bar
     ---------------------------------------------------------------------- */
  function progress() {
    var bar = document.querySelector('[data-vm-progress]');
    if (!bar) return;
    var ticking = false;
    function run() {
      var max = document.documentElement.scrollHeight - window.innerHeight;
      var p = max > 0 ? window.scrollY / max : 0;
      bar.style.transform = 'scaleX(' + p.toFixed(4) + ')';
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(run);
    }, { passive: true });
    run();
  }

  /* ----------------------------------------------------------------------
     Tabbed panels (home collection filters)
     ---------------------------------------------------------------------- */
  function tabs() {
    document.querySelectorAll('[data-vm-tabs]').forEach(function (wrap) {
      var btns = wrap.querySelectorAll('[data-vm-tab]');
      var panels = wrap.querySelectorAll('[data-vm-panel]');
      btns.forEach(function (b) {
        b.addEventListener('click', function () {
          var key = b.getAttribute('data-vm-tab');
          btns.forEach(function (x) {
            var on = x === b;
            x.classList.toggle('is-active', on);
            x.setAttribute('aria-selected', on ? 'true' : 'false');
          });
          panels.forEach(function (p) {
            var on = p.getAttribute('data-vm-panel') === key;
            p.hidden = !on;
            if (on && document.visibilityState === 'visible') {
              p.querySelectorAll('[data-vm-in]').forEach(function (el, i) {
                el.style.setProperty('--vm-d', i * 70 + 'ms');
                hide(el);
                requestAnimationFrame(function () {
                  requestAnimationFrame(function () { show(el); });
                });
              });
            }
          });
        });
      });
    });
  }


  /* ----------------------------------------------------------------------
     Carousel

     Paging sets a single custom property on the track; the movement itself is
     a CSS transition on `transform`, so it runs on the compositor with no
     per-frame JavaScript, no layout and no paint. The previous version tweened
     `scrollLeft` by hand and blurred every card on the way — that is what made
     it stutter.

     Touch devices fall back to native horizontal scrolling: swiping is the
     right gesture there and the browser's inertia beats anything scripted.
     ---------------------------------------------------------------------- */
  function carousels() {
    document.querySelectorAll('[data-vm-carousel]').forEach(function (root) {
      if (root.dataset.vmCarouselReady) {
        if (root._vmSync) root._vmSync();
        return;
      }
      root.dataset.vmCarouselReady = '1';

      var viewport = root.querySelector('.vm-carousel__viewport');
      var track = root.querySelector('.vm-carousel__track');
      if (!viewport || !track) return;

      var scope = root.closest('section') || document;
      var prev = scope.querySelector('[data-vm-prev]');
      var next = scope.querySelector('[data-vm-next]');
      var x = 0;

      // The stylesheet decides which mode we're in, so there's one source of truth.
      function scrollMode() {
        return getComputedStyle(viewport).overflowX !== 'hidden';
      }
      function maxX() {
        return Math.max(0, track.scrollWidth - viewport.clientWidth);
      }
      function current() {
        return scrollMode() ? viewport.scrollLeft : x;
      }

      function step() {
        var slide = track.querySelector('.vm-carousel__slide');
        if (!slide) return viewport.clientWidth * 0.8;
        var gap = parseFloat(getComputedStyle(track).columnGap || 0) || 0;
        var w = slide.getBoundingClientRect().width + gap;
        // Always travel a whole number of cards.
        return Math.max(w, Math.floor(viewport.clientWidth / w) * w);
      }

      function sync() {
        var at = current();
        var max = maxX();
        if (prev) prev.disabled = at < 4;
        if (next) next.disabled = at > max - 4;
        root.classList.toggle('is-start', at < 4);
        root.classList.toggle('is-end', at > max - 4);
      }

      function goTo(target) {
        var to = Math.max(0, Math.min(maxX(), target));
        if (scrollMode()) {
          viewport.scrollTo({ left: to, behavior: reduced ? 'auto' : 'smooth' });
        } else {
          x = to;
          track.style.setProperty('--vm-x', to);
        }
        sync();
      }

      function reset() {
        // A resize can shrink the track under us; keep the offset legal.
        if (!scrollMode() && x > maxX()) goTo(maxX());
        else sync();
      }

      if (next) next.addEventListener('click', function () { goTo(current() + step()); });
      if (prev) prev.addEventListener('click', function () { goTo(current() - step()); });

      viewport.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', reset);

      root._vmSync = reset;
      if ('MutationObserver' in window) {
        new MutationObserver(reset).observe(track, { childList: true });
      }
      if ('ResizeObserver' in window) {
        new ResizeObserver(reset).observe(viewport);
      }

      root.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current() + step()); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current() - step()); }
      });

      sync();
    });
  }

  /* ----------------------------------------------------------------------
     Quantity steppers
     ---------------------------------------------------------------------- */
  function qty(scope) {
    (scope || document).querySelectorAll('[data-vm-qty]').forEach(function (box) {
      if (box.dataset.vmQtyDone) return;
      box.dataset.vmQtyDone = '1';
      var input = box.querySelector('input');
      box.querySelectorAll('[data-vm-step]').forEach(function (b) {
        b.addEventListener('click', function () {
          var step = parseInt(b.getAttribute('data-vm-step'), 10);
          var min = parseInt(input.getAttribute('min') || '1', 10);
          var next = Math.max(min, (parseInt(input.value, 10) || min) + step);
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* ----------------------------------------------------------------------
     Toast
     ---------------------------------------------------------------------- */
  var toastEl;
  function toast(msg, tone) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.setAttribute('role', 'status');
      toastEl.style.cssText =
        'position:fixed;left:50%;bottom:24px;transform:translate(-50%,140%);z-index:200;' +
        'padding:13px 22px;border-radius:999px;background:#0a0a0b;color:#fff;' +
        'font:600 12px/1 var(--vm-font-body);letter-spacing:.08em;text-transform:uppercase;' +
        'box-shadow:0 18px 40px -18px rgba(0,0,0,.6);transition:transform .6s cubic-bezier(.16,1.24,.3,1);' +
        'max-width:88vw;text-align:center;';
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.style.background = tone === 'err' ? '#c0392b' : '#0a0a0b';
    requestAnimationFrame(function () {
      toastEl.style.transform = 'translate(-50%,0)';
    });
    clearTimeout(toastEl._t);
    toastEl._t = setTimeout(function () {
      toastEl.style.transform = 'translate(-50%,140%)';
    }, 2600);
  }

  /* ----------------------------------------------------------------------
     Cart count + AJAX add
     ---------------------------------------------------------------------- */
  function setCount(n) {
    document.querySelectorAll('[data-vm-cart-count]').forEach(function (el) {
      el.textContent = n;
      el.setAttribute('data-count', n);
    });
  }

  function refreshCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { setCount(c.item_count); return c; })
      .catch(function () {});
  }

  function quickAdd() {
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-vm-add]');
      if (!form) return;
      e.preventDefault();
      var btn = form.querySelector('[type="submit"]');
      var label = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error(res.j.description || res.j.message || 'Could not add to cart');
          return refreshCart().then(function () {
            toast('Added to bag');
            document.dispatchEvent(new CustomEvent('vm:cart:added', { detail: res.j }));
          });
        })
        .catch(function (err) { toast(err.message || 'Something went wrong', 'err'); })
        .finally(function () {
          if (btn) { btn.disabled = false; btn.textContent = label; }
        });
    });
  }

  /* ----------------------------------------------------------------------
     Product page — variant picker
     ---------------------------------------------------------------------- */
  function variants() {
    document.querySelectorAll('[data-vm-variants]').forEach(function (form) {
      var data = form.querySelector('[data-vm-variant-json]');
      if (!data) return;
      var list;
      try { list = JSON.parse(data.textContent); } catch (e) { return; }

      var idInput = form.querySelector('[data-vm-variant-id]');
      var priceEl = form.querySelector('[data-vm-price]');
      var compareEl = form.querySelector('[data-vm-compare]');
      var saveEl = form.querySelector('[data-vm-save]');
      var addBtn = form.querySelector('[data-vm-add-btn]');
      var stockEl = form.querySelector('[data-vm-stock]');
      var money = (window.VM && window.VM.moneyFormat) || '${{amount}}';

      function fmt(cents) {
        var v = (cents / 100).toFixed(2);
        var parts = v.split('.');
        var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return money
          .replace(/\{\{\s*amount\s*\}\}/, whole + '.' + parts[1])
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/, whole)
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1])
          .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
      }

      function selected() {
        return Array.prototype.map.call(
          form.querySelectorAll('[data-vm-opt]'),
          function (group) {
            var c = group.querySelector('input:checked');
            return c ? c.value : null;
          }
        );
      }

      function update() {
        var opts = selected();
        var match = list.find(function (v) {
          return v.options.every(function (o, i) { return opts[i] == null || o === opts[i]; });
        });

        // Grey out combinations that don't exist
        form.querySelectorAll('[data-vm-opt]').forEach(function (group, gi) {
          group.querySelectorAll('input').forEach(function (input) {
            var probe = opts.slice();
            probe[gi] = input.value;
            var exists = list.some(function (v) {
              return v.options.every(function (o, i) { return probe[i] == null || o === probe[i]; }) && v.available;
            });
            input.disabled = !exists;
          });
          var label = group.previousElementSibling &&
            group.previousElementSibling.querySelector('[data-vm-opt-value]');
          var checked = group.querySelector('input:checked');
          if (label && checked) label.textContent = checked.value;
        });

        if (!match) {
          if (addBtn) { addBtn.disabled = true; addBtn.textContent = 'Unavailable'; }
          return;
        }
        if (idInput) idInput.value = match.id;
        if (priceEl) priceEl.textContent = fmt(match.price);
        if (compareEl) {
          var on = match.compare_at_price && match.compare_at_price > match.price;
          compareEl.hidden = !on;
          if (on) compareEl.textContent = fmt(match.compare_at_price);
          if (saveEl) {
            saveEl.hidden = !on;
            if (on) saveEl.textContent = '-' + Math.round((1 - match.price / match.compare_at_price) * 100) + '%';
          }
        }
        if (stockEl) stockEl.textContent = match.available ? 'In stock' : 'Sold out';
        if (addBtn) {
          addBtn.disabled = !match.available;
          addBtn.textContent = match.available ? 'Add to bag' : 'Sold out';
        }

        // Jump the gallery to this variant's image
        if (match.featured_media_id) {
          var target = document.querySelector('[data-vm-media="' + match.featured_media_id + '"]');
          if (target) {
            var main = document.querySelector('.vm-gallery__main img');
            var src = target.getAttribute('data-vm-media-src');
            if (main && src) { main.src = src; main.srcset = target.getAttribute('data-vm-media-srcset') || ''; }
          }
        }

        var url = new URL(window.location.href);
        url.searchParams.set('variant', match.id);
        window.history.replaceState({}, '', url);
      }

      form.addEventListener('change', function (e) {
        if (e.target.matches('[data-vm-opt] input')) update();
      });
      update();
    });
  }

  /* ----------------------------------------------------------------------
     Cart page — line updates
     ---------------------------------------------------------------------- */
  function cartPage() {
    var page = document.querySelector('[data-vm-cart]');
    if (!page) return;

    function change(line, quantity) {
      page.classList.add('is-busy');
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      })
        .then(function (r) { return r.json(); })
        .then(function () { window.location.reload(); })
        .catch(function () { toast('Could not update the bag', 'err'); page.classList.remove('is-busy'); });
    }

    page.addEventListener('click', function (e) {
      var rm = e.target.closest('[data-vm-remove]');
      if (rm) { e.preventDefault(); change(parseInt(rm.getAttribute('data-vm-remove'), 10), 0); }
    });
    page.addEventListener('change', function (e) {
      var input = e.target.closest('[data-vm-line]');
      if (!input) return;
      change(parseInt(input.getAttribute('data-vm-line'), 10), parseInt(input.value, 10) || 0);
    });
  }

  /* ----------------------------------------------------------------------
     Collection facets — auto-submit the filter form
     ---------------------------------------------------------------------- */
  function facets() {
    var form = document.querySelector('[data-vm-facets]');
    if (form) {
      form.addEventListener('change', function () { form.submit(); });
    }
    var toggle = document.querySelector('[data-vm-facets-toggle]');
    var side = document.querySelector('[data-vm-facets-side]');
    if (toggle && side) {
      toggle.addEventListener('click', function () {
        var open = side.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
    var sort = document.querySelector('[data-vm-sort]');
    if (sort) {
      sort.addEventListener('change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', sort.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    }
  }

  /* ----------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */
  function boot() {
    prepareSplits();
    watchFit();
    applyStagger();
    arm();
    unlockPaint();
    observe();
    revealSafety();
    header();
    drawer();
    marquees();
    parallax();
    magnets();
    progress();
    tabs();
    qty();
    carousels();
    quickAdd();
    variants();
    cartPage();
    facets();
    refreshCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-run on Shopify theme-editor section reloads
  document.addEventListener('shopify:section:load', function (e) {
    prepareSplits(e.target);
    fitText();
    applyStagger(e.target);
    arm(e.target);
    unlockPaint();
    observe(e.target);
    qty(e.target);
    marquees();
    variants();
    carousels();
  });

  window.VM = window.VM || {};
  window.VM.toast = toast;
  window.VM.refreshCart = refreshCart;
  window.VM.observe = observe;
  window.VM.carousels = carousels;
  window.VM.fitText = fitText;
})();
